import { Component, ViewChild, ChangeDetectorRef, EventEmitter, Output, OnDestroy } from '@angular/core';
import { BdHomepageService } from '../bd-homepage.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { SharedService } from 'src/app/core/services/shared.service';
import { DATE_FORMAT, ERROR_GETTING_DATA } from 'src/app/app.constants';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
} from '@angular/material/legacy-dialog';
import { EmployerEventsFormComponent } from 'src/app/employer/employer-events/employer-events-form/employer-events-form.component';
import { EventModalMode } from 'src/app/shared/enums/event-modal-mode';
import { EmployerService } from 'src/app/employer/employer.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { EmployerEvent, EmployerEventOptions } from 'src/app/shared/interfaces/employer-event';
import { BdHomepageEvent, BdHomepageEvents } from 'src/app/shared/interfaces/homepage-event';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'bd-events',
  templateUrl: './bd-events.component.html',
  styleUrls: ['./bd-events.component.scss'],
})
export class BdEventsComponent implements OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Output() tabChanged = new EventEmitter<string>();

  constructor(
    private readonly bdHomepageService: BdHomepageService,
    private readonly cdr: ChangeDetectorRef,
    public sharedService: SharedService,
    private readonly dialog: MatDialog,
    private readonly employerService: EmployerService,
    private readonly snackBar: MatSnackBar
  ) {}

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  staffId: number;
  permissions: { canEditEvent: boolean; canDeleteEvent: boolean } = {
    canEditEvent: false,
    canDeleteEvent: false,
  };

  loading = false;
  runSetUp = false;
  eventError = false;

  openEvent = false;

  eventsDataSource = new MatTableDataSource<BdHomepageEvent>();

  private filterStaffIds: number[] = [];
  private filterTypeIds: number[] = [];
  private eventOptions: EmployerEventOptions = {
    staff: [],
    hsTypes: [],
    types: [],
    branches: [],
  };

  tabs = ['Today', 'Recent', 'Upcoming', 'Missed'];

  activeTab = 'Today';

  eventColumns = {
    Today: ['rootName', 'assignee', 'type', 'plannedDate', 'actions'],
    Recent: ['rootName', 'assignee', 'type', 'plannedDate', 'actualDate', 'actions'],
    Upcoming: ['rootName', 'assignee', 'type', 'plannedDate', 'actions'],
    Missed: ['rootName', 'assignee', 'type', 'plannedDate', 'actions'],
  };

  dateColumns = ['plannedDate', 'actualDate'];
  dateFormat = DATE_FORMAT;
  dataError = ERROR_GETTING_DATA;

  totalEvents = 0;
  pageSize = 12;
  pageIndex = 0;

  loadingEvent: number | null = null;

  setUp(staffId: number, permissions: { canEditEvent: boolean; canDeleteEvent: boolean }): void {
    this.staffId = staffId;
    this.permissions = permissions;
    this.runSetUp = true;
    this.tabChanged.emit(this.activeTab);
  }

  setEventOptions(options: EmployerEventOptions): void {
    this.eventOptions = { ...options };
  }

  applyFilters(staffIds: number[], typeIds: number[], resetPage = true): void {
    this.filterStaffIds = [...staffIds];
    this.filterTypeIds = [...typeIds];

    if (!this.runSetUp) {
      return;
    }

    this.getEvents(resetPage);
  }

  changeTab(tab: string): void {
    if (this.loading || this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;
    this.tabChanged.emit(this.activeTab);
    this.getEvents();
  }

  handlePageEvent($event: PageEvent): void {
    this.pageIndex = $event.pageIndex;

    this.getEvents(false);
  }

  getEvents(resetPage = true): void {
    if (this.loading) {
      return;
    }

    if (this.staffId === undefined) {
      console.error('Staff ID is required');
      return;
    }

    if (resetPage) {
      this.pageIndex = 0;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
    }

    this.loading = true;
    this.eventError = false;

    const filters = {
      tab: this.activeTab,
      staff: this.filterStaffIds,
      type: this.filterTypeIds,
    };

    this.eventsDataSource.data = [];

    this.bdHomepageService
      .getEvents(this.staffId, filters, this.pageIndex, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: BdHomepageEvents) => {
          this.eventsDataSource.data = data.list;
          this.totalEvents = data.total;
        },
        error: (error) => {
          console.error(error);
          this.eventError = true;
          this.loading = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  addTime(element: BdHomepageEvent): string {
    let time = `, ${element.startTime}`;
    if (element.endTime) {
      time += ` - ${element.endTime}`;
    }
    return time;
  }

  openEventForm(element: BdHomepageEvent): void {
    if (this.openEvent) {
      return;
    }

    this.loadingEvent = element.id;
    this.openEvent = true;

    // As on the H&S homepage: ask the event's business for its options, so the form offers that
    // business's assignees — grouped by company, its own company first (#1807) — while keeping this
    // page's own event types.
    const optionsSubscription = this.employerService
      .getEventFilterOptions(element.rootId, this.staffId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: EmployerEventOptions) => {
          // Until this page's own types have loaded (or if they failed), use the business's, so the
          // required type is never blank in edit mode.
          const types = this.eventOptions.types.length > 0 ? this.eventOptions.types : data.types;
          this.viewEvent(element, { ...data, types });
        },
        error: (error) => {
          console.error(error);
          optionsSubscription.unsubscribe();
          this.openEvent = false;
          this.loadingEvent = null;
          this.snackBar.open(this.dataError, '', {
            duration: 2000,
          });
        },
        complete: () => {
          optionsSubscription.unsubscribe();
        },
      });
  }

  private viewEvent(element: BdHomepageEvent, options: EmployerEventOptions): void {
    const subscription = this.employerService
      .getEvent(element.id, this.staffId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: EmployerEvent) => {
          const event: EmployerEvent = data;

          const eventDialogConfig = new MatDialogConfig();
          eventDialogConfig.width = '650px';
          eventDialogConfig.autoFocus = false;
          eventDialogConfig.disableClose = true;

          eventDialogConfig.data = {
            mode: EventModalMode.view,
            event: event,
            staffId: this.staffId,
            rootId: element.rootId,
            eventOptions: options,
            permissions: {
              canEdit: this.permissions.canEditEvent,
              canDelete: this.permissions.canDeleteEvent,
            },
            showBranch: true,
          };
          this.loadingEvent = null;
          this.cdr.detectChanges();

          this.dialog
            .open(EmployerEventsFormComponent, eventDialogConfig)
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
              if (event?.refresh === true) {
                this.getEvents();
              }
              this.openEvent = false;
            });
        },
        error: (error) => {
          console.error(error);
          subscription.unsubscribe();
          this.openEvent = false;
          this.loadingEvent = null;
          this.snackBar.open(this.dataError, '', {
            duration: 2000,
          });
        },
        complete: () => {
          subscription.unsubscribe();
        },
      });
  }
}
